import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { allTravelDnaSteps } from '@/_constants/TravelDnaStep.ts';
import TravelDnaStep from '@/_constants/TravelDnaStep.ts';
import { dnaSchema } from '$/requests/UpsertDnaRequest.ts';
import type { TravelDnaFormData } from '$/models/Dna.d.ts';
import type { OnCompleteArgs } from '@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx';
import DnaStepLayout from '@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx';
import DnaSlider from '@/components/functional/TravelDnaManipulationForm/shared/DnaSlider.tsx';
import DnaPrimaryButton from '@/components/functional/TravelDnaManipulationForm/shared/DnaPrimaryButton.tsx';
import DnaError from '@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx';

const yourSpendingSchema = dnaSchema.pick({
  spending_preference: true,
});

export type YourSpendingSchema = z.infer<typeof yourSpendingSchema>;

type YourSpendingProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

function YourSpending({ onComplete, onCompleteWhenDirty }: YourSpendingProps) {
  const { t } = useTranslation();
  const { nextStep, setContextData, contextData } = useStepperContext<
    typeof allTravelDnaSteps,
    TravelDnaFormData
  >();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = useForm<YourSpendingSchema>({
    resolver: zodResolver(yourSpendingSchema),
    defaultValues: {
      spending_preference: contextData?.spending_preference ?? 3,
    },
  });

  const value = watch('spending_preference') ?? 3;

  const onSubmit = (data: YourSpendingSchema) => {
    void (async () => {
      try {
        setIsPending(true);
        setError(null);
        const context = { ...contextData, ...data };
        const args: OnCompleteArgs = {
          step: TravelDnaStep.YOUR_SPENDING,
          data,
          context,
        };
        const shouldPersist =
          isDirty || contextData?.spending_preference == null;

        if (shouldPersist) {
          setContextData((previous) => ({ ...previous, ...data }));
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <DnaStepLayout
      eyebrow={t('travelDnaPage.yourSpending.header')}
      title={t('travelDnaPage.yourSpending.subheader')}
      maxWidthClassName="max-w-[770px]"
    >
      <form
        className="mt-24 flex w-full flex-col items-center"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <DnaError error={error} className="mb-7" />
        <Controller
          control={control}
          name="spending_preference"
          render={({ field }) => (
            <DnaSlider
              value={field.value ?? 3}
              onChange={field.onChange}
              leftLabel={t('travelDnaPage.yourSpending.leftLabel')}
              leftDescription={t('travelDnaPage.yourSpending.leftDescription')}
              rightLabel={t('travelDnaPage.yourSpending.rightLabel')}
              rightDescription={t('travelDnaPage.yourSpending.rightDescription')}
              caption={t(`travelDnaPage.yourSpending.captions.${value}`)}
              ticks={['$', '$$', '$$$', '$$$$', '$$$$$']}
              disabled={isPending}
            />
          )}
        />

        <DnaPrimaryButton className="mt-16" loading={isPending} type="submit">
          {t('travelDnaPage.yourSpending.continue')}
        </DnaPrimaryButton>
      </form>
    </DnaStepLayout>
  );
}

export default YourSpending;
